import { formatNumber } from '@angular/common';
import { AfterContentInit, Directive, ElementRef, forwardRef, HostListener, Input, OnChanges, OnDestroy, OnInit, Renderer2 } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { isNullOrUndefined } from './sla-util.helper';

@Directive({
    // eslint-disable-next-line @angular-eslint/directive-selector
    selector: '[numberFormat]',
    providers: [ { provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => NumberDirective), multi: true } ],
})
export class NumberDirective implements OnInit, AfterContentInit, OnChanges, OnDestroy, ControlValueAccessor {

    @Input()
    public numberFormat: string;
    @Input()
    public numberSignum: '+' | '-' | undefined;
    @Input()
    public showThousandSeperator: boolean;
    @Input()
    public locale: string;

    private numberFormatResult: string;
    private modelValue: number;

    private ngUnsubscribe: Subject<void> = new Subject<void>();
    private blurListener: () => void;
    private focusListener: () => void;
    private hasFocus: boolean;

    constructor(private myRenderer2: Renderer2,
                private myElementRef: ElementRef,
                private myTranslateService: TranslateService,
    ) {
    }

    public ngOnInit(): void {
        this._subscribe();
    }

    public ngAfterContentInit(): void {
        const element: HTMLInputElement | HTMLTextAreaElement = this.getInputElement();
        if (element) {
            this.blurListener = this.myRenderer2.listen(element, 'blur', () => {
                this.hasFocus = false;
                this.writeValue(this.modelValue);
                this.onModelTouched();
            });
            this.focusListener = this.myRenderer2.listen(element, 'focus', () => {
                this.hasFocus = true;
            });
        }
    }

    public ngOnDestroy(): void {
        if (this.blurListener) {
            this.blurListener();
        }
        if (this.focusListener) {
            this.focusListener();
        }
        this.ngUnsubscribe.next();
        this.ngUnsubscribe.complete();
    }

    public ngOnChanges(): void {
        const element: HTMLInputElement | HTMLTextAreaElement = this.getInputElement();
        if (element?.value) {
            this.writeValue(element.value);
        }
        this.numberFormatResult = this.numberFormat || '1.0-15';
        this.numberFormatResult = `${this.numberFormatResult.substring(0, 2)}0${this.numberFormatResult.substring(3)}`;
        if (element) {
            if (this.numberFormat?.includes('.0-0')) {
                this.myRenderer2.setProperty(element, 'pattern', '[0-9\\-]*');
                this.myRenderer2.setAttribute(element, 'inputmode', 'numeric');
            } else {
                this.myRenderer2.setProperty(element, 'pattern', '[0-9,.\\-]*');
                this.myRenderer2.setAttribute(element, 'inputmode', 'decimal');
            }
        }
    }

    // eslint-disable-next-line @typescript-eslint/ban-types
    public onModelChange: Function = () => {/**/
    };
    // eslint-disable-next-line @typescript-eslint/ban-types
    public onModelTouched: Function = () => {/**/
    };

    // eslint-disable-next-line @typescript-eslint/member-ordering
    @HostListener('input', [ '$event.target.value' ])
    public onInput(value: any): void {
        const decimal: string = this.getDecimal();
        const thousand: string = this.getThousand();
        const numberFormat: string = this.numberFormatResult;

        // Fix for Samsung Keyboard
        if (value?.includes('.') && decimal !== '.') {
            value = value.replace('.', ',');
        }

        if (value !== undefined && value !== null) {
            if (numberFormat.split('-')[ 1 ] === '0') {
                const regex: RegExp = new RegExp(`[${decimal}]`, 'g');
                value = value.toString().replace(regex, '');
            }
            if (value.toString().split(thousand)[ 1 ]) {
                const regex: RegExp = new RegExp(`[${thousand}]`, 'g');
                value = value.toString().replace(regex, '');
            }
            let valOld: any = '';
            for (let i: number = 0; i < value.toString().length; i++) {
                if (i === 0 && value.toString().charAt(i) === '-') {
                    valOld += value.toString().charAt(i);
                } else if (!isNaN(value.toString().charAt(i)) ||
                    value.toString().charAt(i) === decimal) {
                    valOld += value.toString().charAt(i);
                }
            }
            let val: string = '';
            let decimalIndex: number = -1;
            let decimalCount: number = -1;
            for (let i: number = 0; i < valOld.toString().length; i++) {
                if (valOld.toString().charAt(i) === decimal) {
                    decimalCount = decimalCount + 1;
                    decimalIndex = i;
                } else {
                    val += valOld.toString().charAt(i);
                }
            }
            if (decimalIndex > -1) {
                val = val.substring(0, decimalIndex - decimalCount) + decimal + val.substring(decimalIndex - decimalCount, val.length);
            }
            value = val;
            value = value.replace(decimal + decimal, decimal);
            if (value.toString().split(decimal)[ 1 ]) {
                const regex: RegExp = new RegExp(`[${decimal}]`, 'g');
                value = value.toString().replace(regex, '.');
            }

        }
        this.writeValue(value);
    }

    public writeValue(value: any): void {
        value = this.getWriteValue(value);
        const element: HTMLInputElement | HTMLTextAreaElement = this.getInputElement();
        if (+value !== +this.modelValue) {
            if (element) {
                const decimal: string = this.getDecimal();
                let numberFormat: string = this.numberFormatResult;
                if (!this.hasFocus) {
                    numberFormat = this.numberFormat;
                }
                if (value === null || value === undefined) {
                    this.myRenderer2.setProperty(element, 'value', '');
                } else if (this.showThousandSeperator && (!element.value || !element.value.endsWith(decimal) || numberFormat.split('-')[ 1 ] === '0')) {
                    this.myRenderer2.setProperty(element, 'value', value);
                } else if (!element.value || !element.value.endsWith(decimal) || numberFormat.split('-')[ 1 ] === '0') {
                    let valueTmp: string = value.toString();
                    const regex: RegExp = new RegExp(`[${ this.getThousand() }]`, 'g');
                    valueTmp = valueTmp.replace(regex, '');
                    this.myRenderer2.setProperty(element, 'value', valueTmp);
                }
            }
            this.updateModel(value);
        }

    }

    // eslint-disable-next-line @typescript-eslint/ban-types
    public registerOnChange(fn: Function): void {
        this.onModelChange = fn;
    }

    // eslint-disable-next-line @typescript-eslint/ban-types
    public registerOnTouched(fn: Function): void {
        this.onModelTouched = fn;
    }

    public setDisabledState(isDisabled: boolean): void {
        const element: HTMLInputElement | HTMLTextAreaElement = this.getInputElement();
        if (element) {
            this.myRenderer2.setProperty(element, 'disabled', isDisabled);
        }
    }
    private getWriteValue(value: any): any {
        const element: HTMLInputElement | HTMLTextAreaElement = this.getInputElement();

        let negative: boolean = !isNullOrUndefined(value) && value.toString().charAt(0) === '-';
        if (negative && this.numberSignum === '+' && !isNullOrUndefined(value)) {
            value = value.replace('-', '');
            this.myRenderer2.setProperty(element, 'value', value);
            negative = false;
        } else if (!negative && this.numberSignum === '-' && !isNullOrUndefined(value) && value.toString().length > 0) {
            value = `-${  value}`;
            this.myRenderer2.setProperty(element, 'value', value);
        }
        const decimal: string = this.getDecimal();
        if (value !== undefined && value !== null) {
            value.toString().replace(decimal + decimal, decimal);
            let locale: string = this.locale || this.myTranslateService.instant('sla-core.commons.locale');
            if (locale === 'sla-core.commons.locale') {
                console.warn('sla-core.commons.locale not found in translation file! Using default "de"');
                locale = 'de';
            }
            let numberFormat: string = this.numberFormatResult;
            if (!this.hasFocus) {
                numberFormat = this.numberFormat;
            }
            if (value.toString().split('.')[ 1 ]) {
                let minFraction: any = value.toString().split('.')[ 1 ].length;
                const minFormat: number = Number(numberFormat.split('.')[ 1 ].split('-')[ 0 ]);
                const maxFormat: number = Number(numberFormat.split('.')[ 1 ].split('-')[ 1 ]);

                minFraction = minFraction > minFormat && minFraction < maxFormat ? minFraction : minFormat;
                numberFormat = `${numberFormat.split('.')[ 0 ]}.${minFraction}-${maxFormat}`;
                if (value.toString().split('.')[ 1 ].length > maxFormat) {
                    value = +`${ value.toString().split('.')[ 0 ] }.${ value.toString().split('.')[ 1 ].slice(0, maxFormat) }`;
                }
            }
            if (value.toString().endsWith(decimal)) {
                value = formatNumber(value.slice(0, -1), locale, numberFormat) + decimal;
            } else if (!isNaN(value) && value.toString().length > 0) {
                value = formatNumber(value, locale, numberFormat);
            }
            if (negative && value && value.toString().charAt(0) !== '-') {
                value = `-${value}`;
            }

        }
        return value;
    }
    private _subscribe(): void {
        // subscribe to onTranslationChange event, in case the translations change
        this.myTranslateService.onTranslationChange.pipe(takeUntil(this.ngUnsubscribe)).subscribe(() => {
            this.writeValue(this.modelValue);
        });
        // subscribe to onLangChange event, in case the language changes
        this.myTranslateService.onLangChange.pipe(takeUntil(this.ngUnsubscribe)).subscribe(() => {
            this.writeValue(this.modelValue);
        });

        // subscribe to onDefaultLangChange event, in case the default language changes
        this.myTranslateService.onDefaultLangChange.pipe(takeUntil(this.ngUnsubscribe)).subscribe(() => {
            this.writeValue(this.modelValue);
        });

    }

    private getInputElement(): HTMLInputElement | HTMLTextAreaElement {
        let element: any = this.myElementRef.nativeElement;
        if (!(element instanceof HTMLInputElement) && !(element instanceof HTMLTextAreaElement)) {
            element = element.querySelector('input') || element.querySelector('textarea');
        }
        if (!element) {
            console.warn('Input equals null!');
        }
        return element;
    }

    private updateModel(value: any): void {
        const decimal: string = this.getDecimal();
        const thousand: string = this.getThousand();
        if (value !== undefined && value !== null) {
            value = value.toString();
            if (value.split(thousand)[ 1 ]) {
                const regex: RegExp = new RegExp(`[${thousand}]`, 'g');
                value = value.replace(regex, '');
            }
            if (value.split(decimal)[ 1 ]) {
                const regex: RegExp = new RegExp(`[${decimal}]`, 'g');
                value = value.replace(regex, '.');
            }
            if (value.toString().endsWith(decimal)) {
                const regex: RegExp = new RegExp(`[${decimal}]`, 'g');
                value = value.replace(regex, '');
            }
        }
        if (value !== undefined && value !== null) {
            if (!isNaN(value) && value.toString().length > 0) {
                this.onModelChange(Number(value));
                this.modelValue = Number(value);
            } else {
                this.onModelChange(null);
                this.modelValue = null;
            }
        }
    }

    private getDecimal(): string {
        let locale: string = this.locale || this.myTranslateService.instant('sla-core.commons.locale');
        if (locale === 'sla-core.commons.locale') {
            console.warn('sla-core.commons.locale not found in translation file! Using default "de"');
            locale = 'de';
        }
        let separator: string = formatNumber(1.1, locale, '1.1-1').charAt(1);
        if (this.myTranslateService.instant('sla.ui.commons.decimal-separator.value') !== 'sla.ui.commons.decimal-separator.value') {
            separator = this.myTranslateService.instant('sla.ui.commons.decimal-separator.value');
        }
        return separator;
    }

    private getThousand(): string {
        let locale: string = this.locale || this.myTranslateService.instant('sla-core.commons.locale');
        if (locale === 'sla-core.commons.locale') {
            console.warn('sla-core.commons.locale not found in translation file! Using default "de"');
            locale = 'de';
        }
        let separator: string = formatNumber(100000, locale, '1.0-0').charAt(3);
        if (this.myTranslateService.instant('sla.ui.commons.thousand-separator.value') !== 'sla.ui.commons.thousand-separator.value') {
            separator = this.myTranslateService.instant('sla.ui.commons.thousand-separator.value');
        }
        return separator;
    }

}
